import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle, Building2, User, MapPin, RefreshCw, Clock } from "lucide-react";
import { SiFacebook, SiInstagram, SiGoogle } from "react-icons/si";
import { FaLinkedinIn as SiLinkedin } from "react-icons/fa";
import { apiRequest } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PlatformSelectionModalProps {
  platform: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess: (platform: string) => void;
}

interface PendingData {
  platform: string;
  hasData: boolean;
  expiresAt: number;
  remainingMs: number;
  pages?: any[];
  locations?: any[];
  userProfile?: any;
  organizations?: any[];
  hasOrganizations?: boolean;
  instagramFiltered?: boolean;
}

const platformIcons: Record<string, any> = {
  facebook: SiFacebook,
  instagram: SiInstagram,
  linkedin: SiLinkedin,
  googlebusiness: SiGoogle,
};

const platformNames: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  googlebusiness: "Google My Business",
};

export function PlatformSelectionModal({ platform, open, onClose, onSuccess }: PlatformSelectionModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [pendingData, setPendingData] = useState<PendingData | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(0);

  const fetchPendingData = useCallback(async () => {
    if (!platform) return;

    setLoading(true);
    setError(null);
    setTokenExpired(false);

    try {
      const response = await fetch(`/api/late/connect/${platform}/pending`, {
        credentials: "include",
      });

      if (response.status === 404) {
        setTokenExpired(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      const data: PendingData = await response.json();
      setPendingData(data);
      setRemainingTime(Math.max(0, data.remainingMs));
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, [platform]);

  useEffect(() => {
    if (open && platform) {
      fetchPendingData();
      setSelectedItem(null);
    }
  }, [open, platform, fetchPendingData]);

  useEffect(() => {
    if (remainingTime <= 0 || !open) return;

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          setTokenExpired(true);
          clearInterval(interval);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingTime, open]);

  const handleSubmit = async () => {
    if (!platform || !selectedItem) return;

    setSubmitting(true);
    setError(null);

    try {
      let endpoint = "";
      let body: any = {};

      if (platform === "facebook" || platform === "instagram") {
        endpoint = "/api/late/connect/facebook/select-page";
        body = { pageId: selectedItem, platform };
      } else if (platform === "linkedin") {
        endpoint = "/api/late/connect/linkedin/select-organization";
        body = { accountType: selectedItem };
      } else if (platform === "googlebusiness") {
        endpoint = "/api/late/connect/googlebusiness/select-location";
        body = { locationId: selectedItem };
      }

      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData.error === "token_expired") {
          setTokenExpired(true);
          return;
        }
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      toast({
        title: "Verbindung erfolgreich",
        description: `${platformNames[platform] || platform} wurde erfolgreich verbunden.`,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/late/accounts"] });
      onSuccess(platform);
      onClose();
    } catch (err: any) {
      setError(err.message || "Verbindung konnte nicht abgeschlossen werden");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestart = () => {
    onClose();
    window.location.href = `/platform-connections`;
  };

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const PlatformIcon = platform ? platformIcons[platform] : null;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Lade Optionen...</p>
        </div>
      );
    }

    if (tokenExpired) {
      return (
        <div className="flex flex-col items-center justify-center py-8 gap-4">
          <div className="rounded-full bg-orange-100 dark:bg-orange-950 p-3">
            <Clock className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-center space-y-2">
            <h3 className="font-medium">Sitzung abgelaufen</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Die Verbindungssitzung ist abgelaufen. Bitte starten Sie den Vorgang erneut.
            </p>
          </div>
          <Button onClick={handleRestart} variant="default">
            <RefreshCw className="w-4 h-4 mr-2" />
            Erneut verbinden
          </Button>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-8 gap-4">
          <div className="rounded-full bg-destructive/10 p-3">
            <AlertCircle className="w-6 h-6 text-destructive" />
          </div>
          <div className="text-center space-y-2">
            <h3 className="font-medium">Fehler aufgetreten</h3>
            <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchPendingData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Erneut versuchen
            </Button>
            <Button onClick={handleRestart} variant="default">
              Neu verbinden
            </Button>
          </div>
        </div>
      );
    }

    if (!pendingData) return null;

    if (platform === "linkedin") {
      return renderLinkedInSelection();
    }

    if (platform === "facebook" || platform === "instagram") {
      return renderPageSelection();
    }

    if (platform === "googlebusiness") {
      return renderLocationSelection();
    }

    return null;
  };

  const renderLinkedInSelection = () => {
    if (!pendingData) return null;

    const orgs = pendingData.organizations || [];
    const profile = pendingData.userProfile;

    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Waehlen Sie, ob Sie als Person oder als Unternehmen posten moechten.
        </p>

        <div className="space-y-2">
          <Card
            className={`cursor-pointer transition-all ${selectedItem === "personal" ? "ring-2 ring-primary" : ""} hover-elevate`}
            onClick={() => setSelectedItem("personal")}
          >
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-full bg-blue-100 dark:bg-blue-950 p-2">
                <User className="w-5 h-5 text-blue-700 dark:text-blue-300" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Persoenliches Profil</p>
                <p className="text-sm text-muted-foreground">
                  {profile?.name || profile?.localizedFirstName || "Ihr LinkedIn-Profil"}
                </p>
              </div>
              {selectedItem === "personal" && (
                <Badge variant="default" className="bg-primary text-primary-foreground">
                  Ausgewaehlt
                </Badge>
              )}
            </CardContent>
          </Card>

          {orgs.map((org: any) => {
            const orgId = org.id || org.organizationId || org.$URN;
            return (
              <Card
                key={orgId}
                className={`cursor-pointer transition-all ${selectedItem === orgId ? "ring-2 ring-primary" : ""} hover-elevate`}
                onClick={() => setSelectedItem(orgId)}
              >
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="rounded-full bg-indigo-100 dark:bg-indigo-950 p-2">
                    <Building2 className="w-5 h-5 text-indigo-700 dark:text-indigo-300" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{org.name || org.localizedName || "Unternehmen"}</p>
                    {org.vanityName && (
                      <p className="text-sm text-muted-foreground">@{org.vanityName}</p>
                    )}
                  </div>
                  {selectedItem === orgId && (
                    <Badge variant="default" className="bg-primary text-primary-foreground">
                      Ausgewaehlt
                    </Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {orgs.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">
              Keine Unternehmensseiten gefunden. Sie koennen Ihr persoenliches Profil verwenden.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderPageSelection = () => {
    if (!pendingData) return null;

    const pages = pendingData.pages || [];

    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {platform === "instagram"
            ? "Waehlen Sie die Facebook-Seite, die mit Ihrem Instagram Business-Konto verknuepft ist."
            : "Waehlen Sie die Facebook-Seite, die Sie verbinden moechten."}
        </p>

        {pages.length === 0 ? (
          <div className="text-center py-6">
            <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {platform === "instagram"
                ? "Keine Facebook-Seiten mit verknuepftem Instagram Business-Konto gefunden."
                : "Keine Facebook-Seiten gefunden."}
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {pages.map((page: any) => {
              const pageId = page.id || page.pageId;
              return (
                <Card
                  key={pageId}
                  className={`cursor-pointer transition-all ${selectedItem === pageId ? "ring-2 ring-primary" : ""} hover-elevate`}
                  onClick={() => setSelectedItem(pageId)}
                >
                  <CardContent className="flex items-center gap-3 p-4">
                    {page.picture?.data?.url || page.pictureUrl ? (
                      <img
                        src={page.picture?.data?.url || page.pictureUrl}
                        alt={page.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="rounded-full bg-blue-100 dark:bg-blue-950 p-2">
                        <SiFacebook className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{page.name}</p>
                      {page.category && (
                        <p className="text-sm text-muted-foreground truncate">{page.category}</p>
                      )}
                    </div>
                    {selectedItem === pageId && (
                      <Badge variant="default" className="bg-primary text-primary-foreground flex-shrink-0">
                        Ausgewaehlt
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderLocationSelection = () => {
    if (!pendingData) return null;

    const locations = pendingData.locations || [];

    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Waehlen Sie den Google Business-Standort, den Sie verbinden moechten.
        </p>

        {locations.length === 0 ? (
          <div className="text-center py-6">
            <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Keine Google Business-Standorte gefunden.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {locations.map((location: any) => {
              const locId = location.id || location.locationId || location.name;
              return (
                <Card
                  key={locId}
                  className={`cursor-pointer transition-all ${selectedItem === locId ? "ring-2 ring-primary" : ""} hover-elevate`}
                  onClick={() => setSelectedItem(locId)}
                >
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="rounded-full bg-green-100 dark:bg-green-950 p-2">
                      <MapPin className="w-5 h-5 text-green-700 dark:text-green-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{location.title || location.locationName || location.name}</p>
                      {location.address && (
                        <p className="text-sm text-muted-foreground truncate">
                          {typeof location.address === "string"
                            ? location.address
                            : [location.address.addressLines?.[0], location.address.locality].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                    {selectedItem === locId && (
                      <Badge variant="default" className="bg-primary text-primary-foreground flex-shrink-0">
                        Ausgewaehlt
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const getDialogTitle = () => {
    if (!platform) return "";
    const name = platformNames[platform] || platform;

    if (platform === "linkedin") return `${name} - Profil waehlen`;
    if (platform === "facebook" || platform === "instagram") return `${name} - Seite waehlen`;
    if (platform === "googlebusiness") return `${name} - Standort waehlen`;
    return `${name} verbinden`;
  };

  const getDialogDescription = () => {
    if (tokenExpired) return "Die Sitzung ist abgelaufen.";
    if (!platform) return "";
    if (platform === "linkedin")
      return "Waehlen Sie, wie Sie auf LinkedIn posten moechten.";
    if (platform === "facebook" || platform === "instagram")
      return "Waehlen Sie die Seite fuer Ihre Verbindung.";
    if (platform === "googlebusiness")
      return "Waehlen Sie den Standort fuer Ihre Verbindung.";
    return "";
  };

  const canSubmit = selectedItem && !submitting && !tokenExpired && !error;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {PlatformIcon && <PlatformIcon className="w-5 h-5" />}
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        {!tokenExpired && !loading && remainingTime > 0 && (
          <div className={`flex items-center justify-end gap-1 text-xs ${
            remainingTime < 120000 ? 'text-orange-600 font-medium' : 'text-muted-foreground'
          }`}>
            <Clock className="w-3 h-3" />
            <span>Verbleibend: {formatTime(remainingTime)}</span>
          </div>
        )}

        {renderContent()}

        {!tokenExpired && !loading && !error && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Abbrechen
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird verbunden...
                </>
              ) : (
                "Verbindung abschliessen"
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
